// components/profile/ProfilePage.jsx
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart3,
  Award,
  CalendarDays,
  ActivityIcon,
  Cog,
  Swords,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import avatarDefault from "@/assets/images/profile/avatar_photo.png";
import backgroundDefault from "@/assets/images/profile/background_photo.jpg";
import { getImageUrl } from "@/utils/imageUrl";

// Section imports
import Dashboard from "./Dashboard";
import Achievements from "./Achievements";
import EventHistory from "./EventHistory";
import Activity from "./Activity"; 
import Settings from "./Settings"; 
import Armoury from "./Armoury";

const tabs = [
  { key: "dashboard", label: "Dashboard", icon: <BarChart3 /> },
  { key: "achievements", label: "Achievements", icon: <Award /> },
  { key: "events", label: "Event History", icon: <CalendarDays /> },
  { key: "activity", label: "Activity", icon: <ActivityIcon /> }, 
  { key: "armoury", label: "Armoury", icon: <Swords /> },
  { key: "settings", label: "Settings", icon: <Cog /> }, 
];

const ProfilePage = ({ initialTab = "dashboard" }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);

  // Render content dynamically
  const renderSection = () => {
    switch (activeTab) {
      case "dashboard":
        return <Dashboard />;
      case "achievements":
        return <Achievements />;
      case "events":
        return <EventHistory />;
      case "activity":
        return <Activity />; 
      case "armoury":
        return <Armoury />;
      case "settings":
        return <Settings />; 
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* ---------- HERO SECTION (info inside cover) ---------- */}
      <div className="relative w-full h-[28rem] md:h-[32rem] bg-gray-50">
        {/* Background Image */}
        <img
          src={getImageUrl(user?.cover_photo_url) || backgroundDefault}
          alt="Profile cover"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Slight dark overlay for contrast (optional) */}
        <div className="absolute inset-0 bg-black/40" />

        {/* CONTENT (avatar + info) */}
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-10 text-center text-white z-20">
          {/* Avatar */}
          <div className="relative -mb-16">
            <img
              src={getImageUrl(user?.avatar_url) || avatarDefault}
              alt="Profile avatar"
              className="w-32 h-32 md:w-36 md:h-36 rounded-full border-4 border-white dark:border-gray-800 shadow-lg object-cover transition-colors duration-300"
            />
          </div>

          {/* Info text inside the same section */}
          <div className="mt-20 text-black dark:text-gray-100 bg-white/70 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl px-6 py-4 shadow-sm max-w-2xl">
            <h1 className="text-2xl md:text-3xl font-bold">
              {user?.name || "Rider Name"}
            </h1>
            <p className="text-sm text-gray-700 dark:text-gray-300 opacity-90">
              @{user?.username}
            </p>
            <p className="text-sm md:text-base opacity-90">
              {typeof user?.membership === "object"
                ? user?.membership?.plan
                : user?.membership || "Rider"}{" "}
              ·{" "}
              {typeof user?.location === "object"
                ? user?.location?.city || user?.location?.name
                : user?.location || "Unknown location"}
            </p>

            <p className="text-[15px] text-gray-700 dark:text-gray-300 mt-2 italic leading-relaxed">
              {user?.bio && user.bio.trim() !== ""
                ? `${user.bio}`
                : `Cycling enthusiast exploring new routes and long rides.`}
            </p>

            <div className="mt-3 text-sm text-gray-700 dark:text-gray-400 flex flex-col sm:flex-row items-center justify-center gap-2">
              <span className="capitalize">
                {user?.gender
                  ? `Gender: ${user.gender}`
                  : "Gender: Not specified"}
              </span>
              <span className="hidden sm:inline">•</span>
              <span>
                {user?.date_of_birth
                  ? `Date of Birth: ${new Date(
                      user.date_of_birth
                    ).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}`
                  : "Date of Birth: Not set"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- TAB NAVIGATION ---------- */}
      <div className="mt-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="flex flex-nowrap md:justify-center items-center gap-2 md:gap-4 px-4 md:px-6 py-4 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center justify-center gap-3 px-8 py-4 text-base font-semibold rounded-full transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.key
                  ? "bg-gradient-to-r from-[#1e8a3b] to-[#114a1f] text-white shadow-lg shadow-green-900/20 scale-105"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              <span
                className={`w-5 h-5 ${
                  activeTab === tab.key
                    ? "text-white"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {tab.icon}
              </span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ---------- MAIN CONTENT ---------- */}
      <main className="flex-1 container mx-auto px-4 md:px-10 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
          >
            {renderSection()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ProfilePage;
